import React, { useState, useEffect } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { FaUpload, FaSpinner, FaCheck, FaFilePdf, FaEye, FaTimes } from 'react-icons/fa';

const SimplePDFUploader = () => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadedPages, setUploadedPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUploadedPages();
  }, []);

  const loadUploadedPages = async () => {
    try {
      setLoading(true);
      const pagesRef = ref(storage, `pdf-pages/${user.uid}/`);
      const result = await listAll(pagesRef);
      
      const pageUrls = [];
      for (const itemRef of result.items) {
        const url = await getDownloadURL(itemRef);
        const pageNumber = parseInt(itemRef.name.replace('page_', '').replace('.png', ''));
        pageUrls.push({ pageNumber, url, name: itemRef.name });
      }
      
      pageUrls.sort((a, b) => a.pageNumber - b.pageNumber);
      setUploadedPages(pageUrls);
      console.log('📄 Loaded uploaded pages:', pageUrls.length);
    } catch (error) {
      console.error('❌ Error loading uploaded pages:', error);
      setError('Failed to load uploaded pages');
    } finally {
      setLoading(false);
    }
  };

  const handlePageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const pageNumber = uploadedPages.length + 1;
      const fileName = `page_${pageNumber}.png`; // PNG for best quality
      const storageRef = ref(storage, `pdf-pages/${user.uid}/${fileName}`);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newPage = {
        pageNumber,
        url: downloadURL,
        name: fileName
      };

      setUploadedPages(prev => [...prev, newPage]);
      console.log('✅ Page uploaded successfully:', fileName);

    } catch (error) {
      console.error('❌ Error uploading page:', error);
      setError('Failed to upload page');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePage = async (pageNumber) => {
    try {
      const pageToDelete = uploadedPages.find(p => p.pageNumber === pageNumber);
      if (!pageToDelete) return;

      const storageRef = ref(storage, `pdf-pages/${user.uid}/${pageToDelete.name}`);
      await storageRef.delete();

      setUploadedPages(prev => prev.filter(p => p.pageNumber !== pageNumber));
      console.log('🗑️ Page deleted:', pageNumber);

      if (currentPage > uploadedPages.length - 1) {
        setCurrentPage(Math.max(1, uploadedPages.length - 1));
      }
    } catch (error) {
      console.error('❌ Error deleting page:', error);
      setError('Failed to delete page');
    }
  };

  const nextPage = () => {
    if (currentPage < uploadedPages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 text-purple-500 mx-auto mb-4">
            <FaSpinner size={48} />
          </div>
          <p className="text-purple-700 text-lg">Loading pages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-800 mb-2">📄 Simple PDF Page Uploader</h1>
          <p className="text-purple-600 text-lg">Upload PDF pages as images and view them securely</p>
        </div>

        {/* Upload Section */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border-2 border-purple-200 mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-purple-800 mb-4">Upload PDF Page</h2>
            
            <label className="cursor-pointer inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
              <FaUpload className="mr-2" />
              {uploading ? 'Uploading...' : 'Select Image File'}
              <input
                type="file"
                accept="image/*"
                onChange={handlePageUpload}
                disabled={uploading}
                className="hidden"
              />
            </label>

            {uploading && (
              <div className="mt-4 flex items-center justify-center">
                <FaSpinner className="animate-spin mr-2" />
                <span>Uploading page...</span>
              </div>
            )}

            {error && (
              <div className="mt-4 text-red-600 bg-red-100 p-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="mt-4 text-sm text-gray-600">
              Upload images of your PDF pages one by one
            </div>
          </div>
        </div>

        {/* Pages Display */}
        {uploadedPages.length > 0 && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border-2 border-purple-200">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-purple-800 mb-2">
                Page {currentPage} of {uploadedPages.length}
              </h2>
              
              {/* Navigation */}
              <div className="flex justify-center gap-4 mb-6">
                <button
                  onClick={prevPage}
                  disabled={currentPage <= 1}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:bg-gray-400 hover:bg-purple-700 transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={nextPage}
                  disabled={currentPage >= uploadedPages.length}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:bg-gray-400 hover:bg-purple-700 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>

            {/* Current Page Display */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <img
                  src={uploadedPages[currentPage - 1]?.url}
                  alt={`Page ${currentPage}`}
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  style={{ maxHeight: '70vh' }}
                  onContextMenu={(e) => e.preventDefault()} // Prevent right-click
                  draggable={false} // Prevent drag
                />
                
                {/* Delete Button */}
                <button
                  onClick={() => handleDeletePage(currentPage)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                  title="Delete this page"
                >
                  <FaTimes size={16} />
                </button>
              </div>
            </div>

            {/* Page Thumbnails */}
            <div className="flex flex-wrap justify-center gap-2">
              {uploadedPages.map((page, index) => (
                <button
                  key={page.pageNumber}
                  onClick={() => setCurrentPage(page.pageNumber)}
                  className={`relative p-1 rounded border-2 transition-colors ${
                    currentPage === page.pageNumber
                      ? 'border-purple-600 bg-purple-100'
                      : 'border-gray-300 hover:border-purple-400'
                  }`}
                >
                  <img
                    src={page.url}
                    alt={`Page ${page.pageNumber}`}
                    className="w-16 h-20 object-cover rounded"
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs text-center py-1 rounded-b">
                    {page.pageNumber}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No Pages Message */}
        {uploadedPages.length === 0 && !loading && (
          <div className="text-center py-12">
            <FaFilePdf className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No pages uploaded yet</h3>
            <p className="text-gray-500">Upload your first PDF page image to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplePDFUploader;
